import React, { useEffect, useState } from 'react';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import { Layout } from 'components/Layout';
import { useRouter } from 'next/router';
import { trackConversion, setUserProperties, identifyUser } from '@/lib/posthog';
import { getUTMParams } from '@/lib/posthog-helpers';

const HolidayGift = () => {
    const router = useRouter();
    const [tracked, setTracked] = useState(false);

    useEffect(() => {
        // Only track once, after router is ready
        if (!router.isReady || tracked) return;

        const utmParams = getUTMParams();
        const email = decodeURIComponent(utmParams.utm_content || (router.query.utm_content as string) || '');

        // Identify user by email (required for Klaviyo sync)
        if (email) {
            identifyUser(email, { email });
        }

        // Fire conversion event with all UTM params
        trackConversion('holiday_offer_claimed', {
            campaign: 'holiday_free_month_2024',
            email: email,
            ...utmParams,
            claimed_at: new Date().toISOString(),
        });

        // Set person properties for easy filtering
        setUserProperties({
            holiday_2024_claimed: true,
            holiday_2024_claimed_at: new Date().toISOString(),
            ...(email && { email }),
        });

        setTracked(true);
    }, [router.isReady, router.query, tracked]);

    return (
        <Layout showClassCodeInput={false} hideNavigationMenu={false} showLoginAndSignup={true}>
            <NextSeo
                title="Your Holiday Gift from Muzology"
                description="Claim your free month of Muzology"
                noindex={true}
                nofollow={true}
            />
            <Head>
                <meta name="robots" content="noindex, nofollow" />
            </Head>

            <div className="bg-home-background w-full min-h-[100vh] font-Lexend relative z-0 pt-[80px] xl:pt-[110px] flex flex-col items-center">
                {/* Decorative blur effects */}
                <div className="hidden md:block absolute top-[-50px] right-0 rounded-full opacity-[1.4] bg-[#B031FA] bg-opacity-[0.7] blur-[250px] w-[729px] h-[585px] z-[1]" />
                <div className="hidden md:block absolute top-[-50px] left-0 rounded-full opacity-[1.4] bg-[#161042] bg-opacity-[0.7] blur-[250px] w-[729px] h-[585px] z-[1]" />

                <div className="p-8 bg-white rounded-xl shadow-xl z-10 w-full max-w-xl mt-10 text-center mx-4">
                    {/* Holiday-themed header */}
                    <div className="text-6xl mb-6">🎁</div>
                    <h1 className="text-3xl font-bold text-gray-800 mb-4">
                        Happy Holidays!
                    </h1>
                    <h2 className="text-xl text-gray-600 mb-6">
                        Your free month of Muzology is on its way!
                    </h2>

                    <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-6 mb-6">
                        <p className="text-gray-700 mb-4">
                            Thank you for being part of the Muzology family!
                            As our gift to you, we are adding a <strong>FREE month</strong> to your subscription.
                        </p>
                        <p className="text-gray-600 text-sm">
                            Your free month will be applied within 24-48 hours.
                            No action needed on your part!
                        </p>
                    </div>

                    <div className="border-t pt-6">
                        <p className="text-gray-500 text-sm mb-4">
                            Questions? Contact us at support@muzology.com
                        </p>
                        <a
                            href="https://app.muzology.com"
                            className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
                        >
                            Go to Muzology
                        </a>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default HolidayGift;
